/**
 * Itinerary API
 *
 * Low-level API calls for Itinerary entity.
 * This is the infrastructure layer - handles HTTP requests.
 *
 * API Changes (Breaking):
 * - POST/PATCH: `itinerary_tour` renamed to `booking_items`
 * - POST/PATCH: Transportation fields moved to `custom_field_values` object
 * - GET: `itinerary_tour` removed, data now in `slots_itineraries`
 * - GET: Each slot includes `custom_field_values` and service.service_type
 *
 * Note: This file coexists with the legacy src/services/admin/itinerary-service.js
 * to allow gradual migration. New code should use this file.
 */
import api from "./axios";
// =============================================================================
// ITINERARY API
// =============================================================================
const ENDPOINT = "itinerary";
function stripClientOwnershipFields(dto) {
    const payload = { ...dto };
    delete payload.created_by;
    delete payload.updated_by;
    delete payload.org_id;
    delete payload.state;
    delete payload.expires_at;
    delete payload.state_updated_at;
    delete payload.updated_at;
    return payload;
}
export const itineraryApi = {
    resendEmail(id, email, requestId) {
        return api.post(`/api/v2/itineraries/${id}/email-resends`, { email }, {
            headers: { 'Idempotency-Key': requestId }, orgContext: 'tenant',
        });
    },
    /**
     * Get all itineraries for the active organization context.
     * Response includes slots_itineraries with custom_field_values
     */
    getItineraries() {
        return api.get(`/${ENDPOINT}`);
    },
    /**
     * Get provider-owned booking projections for the selected provider org.
     */
    getProviderBookings() {
        return api.get(`/${ENDPOINT}/provider-bookings`);
    },
    /**
     * Get single itinerary by ID
     */
    getById(id) {
        return api.get(`/${ENDPOINT}/${id}`);
    },
    /**
     * Get the typed frontend projection used to generate itinerary PDF downloads.
     */
    getPdfProjection(id) {
        return api.get(`/${ENDPOINT}/${id}/pdf-projection`);
    },
    downloadPdf(id) {
        return api.get(`/${ENDPOINT}/${id}/pdf`);
    },
    /**
     * Open the SSE status stream for a pending PDF generation job.
     * Routed through the shared axios instance (fetch adapter) so the
     * existing Authorization/X-Org-Id interceptor applies automatically.
     */
    streamPdfJobEvents(id, jobId, options = {}) {
        return api.get(`/${ENDPOINT}/${id}/pdf/events`, {
            params: { job_id: jobId },
            responseType: "stream",
            adapter: "fetch",
            ...(options.signal ? { signal: options.signal } : {}),
        });
    },
    /**
     * Create new itinerary
     * @param dto - Itinerary data with booking_items
     * @returns Created itinerary with slots_itineraries
     */
    save(dto) {
        return api.post(`/${ENDPOINT}`, stripClientOwnershipFields(dto));
    },
    /**
     * Update existing itinerary
     * @param id - Itinerary ID
     * @param dto - Updated itinerary data with booking_items
     * @returns Updated itinerary with slots_itineraries
     */
    update(id, dto) {
        return api.patch(`/${ENDPOINT}/${id}`, stripClientOwnershipFields(dto));
    },
    /**
     * Transition itinerary state through the backend state machine.
     */
    updateState(id, dto) {
        return api.patch(`/${ENDPOINT}/${id}/state`, dto);
    },
    /**
     * Delete itinerary
     * @param id - Itinerary ID
     */
    delete(id) {
        return api.delete(`/${ENDPOINT}/${id}`);
    },
};
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default itineraryApi;
